// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import type { JSX } from 'react';
import type { DeniedEntry } from '../../hooks/useDeniedQueue';

interface MoveToTestingModalProps {
  opened: boolean;
  entry: DeniedEntry | null;
  onClose: () => void;
  onMove: (ahi: string, note: string) => void;
}

export function MoveToTestingModal({ opened, entry, onClose, onMove }: MoveToTestingModalProps): JSX.Element {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={entry ? `Move to Testing · ${entry.patientName}` : 'Move to Testing'}
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          In-lab PSG results received — begin ongoing adherence monitoring.
        </Text>
        <Alert color="blue" variant="light">
          Moves <b>{entry?.patientName}</b> from the Refer out folder to the Testing stage.
        </Alert>
        <Group grow align="flex-start">
          <TextInput
            label="AHI result"
            description="Optional"
            type="number"
            min={0}
            step={0.1}
            id="move-testing-ahi"
            placeholder="e.g. 24"
          />
          <TextInput label="Referred from" value="In-lab polysomnography" readOnly />
        </Group>
        <Textarea
          label="Results note"
          required
          minRows={4}
          defaultValue={
            entry
              ? `In-lab PSG completed for ${entry.patientName}. Results received; initiating therapy and ongoing adherence monitoring.`
              : ''
          }
          id="move-testing-note"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="polaris"
            onClick={() => {
              const ahi = (document.getElementById('move-testing-ahi') as HTMLInputElement | null)?.value ?? '';
              const note = (document.getElementById('move-testing-note') as HTMLTextAreaElement | null)?.value ?? '';
              if (note.trim()) {
                onMove(ahi, note.trim());
              }
            }}
          >
            Move to Testing
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
